// ============================================================
// ByOurConsent.com -- Navigation Component
// Desktop: full nav bar
// Mobile: hamburger menu with slide-out drawer
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import useWindowWidth from '../hooks/useWindowWidth';
import toast from 'react-hot-toast';

const ShieldIcon = () => (
    <svg width="20" height="24" viewBox="0 0 28 32" fill="none">
        <path d="M14 1L2 6v10c0 8.5 5.2 15.4 12 17.4C20.8 31.4 26 24.5 26 16V6L14 1z"
              fill="#8b1a1a" stroke="#c8beaa" strokeWidth="0.8"/>
        <circle cx="14" cy="17" r="3" fill="none" stroke="#e8dfc8" strokeWidth="1.2"/>
        <line x1="14" y1="12" x2="14" y2="14" stroke="#e8dfc8" strokeWidth="1.2"/>
    </svg>
);

const Nav = () => {
    const { user, logout, isAdmin } = useAuth();
    const { cartCount }             = useCart();
    const navigate                  = useNavigate();
    const location                  = useLocation();
    const width                     = useWindowWidth();
    const isMobile                  = width < 768;

    const [dropOpen,   setDropOpen]   = useState(false);
    const [menuOpen,   setMenuOpen]   = useState(false);
    const dropRef                     = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) {
                setDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close mobile menu on route change
    useEffect(() => { setMenuOpen(false); }, [location]);

    // Prevent body scroll when menu open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    const handleLogout = async () => {
        setDropOpen(false);
        setMenuOpen(false);
        await logout();
        toast.success('Signed out successfully');
        navigate('/');
    };

    const navLinkStyle = ({ isActive }) => ({
        color:          isActive ? '#e8dfc8' : '#b8ad98',
        fontSize:       '12px',
        fontFamily:     'sans-serif',
        textDecoration: 'none',
        letterSpacing:  '0.06em',
        textTransform:  'uppercase',
        borderBottom:   isActive ? '1px solid #8b1a1a' : '1px solid transparent',
        paddingBottom:  '2px'
    });

    const NAV_LINKS = [
        { to: '/',         label: 'Home' },
        { to: '/articles', label: 'Articles' },
        { to: '/civic',    label: 'Take Action' },
        { to: '/forum',    label: 'Forum' },
        { to: '/store',    label: 'Store' },
        { to: '/chapters', label: 'Chapters' },
    ];

    return (
        <>
            <nav style={{
                background:     '#1e2a3a',
                height:         '56px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                padding:        '0 1.5rem',
                position:       'sticky',
                top:            0,
                zIndex:         500,
                borderBottom:   menuOpen ? 'none' : '0.5px solid rgba(255,255,255,0.06)'
            }}>
                {/* Brand */}
                <Link to="/" style={{
                    color:          '#e8dfc8',
                    fontSize:       '17px',
                    fontWeight:     600,
                    fontFamily:     "'EB Garamond', Georgia, serif",
                    textDecoration: 'none',
                    display:        'flex',
                    alignItems:     'center',
                    gap:            '9px'
                }}>
                    <ShieldIcon />
                    {!isMobile && 'By Our Consent'}
                    {isMobile && <span style={{ fontSize:'15px' }}>By Our Consent</span>}
                </Link>

                {/* Desktop nav links */}
                {!isMobile && (
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        {NAV_LINKS.map(({ to, label }) => (
                            <NavLink key={to} to={to} style={navLinkStyle}>{label}</NavLink>
                        ))}
                    </div>
                )}

                {/* Right side */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

                    {/* Cart */}
                    <Link to="/cart" style={{ color: '#b8ad98', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                            <line x1="3" y1="6" x2="21" y2="6"/>
                            <path d="M16 10a4 4 0 01-8 0"/>
                        </svg>
                        {cartCount > 0 && (
                            <span style={{ background: '#8b1a1a', color: '#f5f0e8', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {cartCount}
                            </span>
                        )}
                    </Link>

                    {/* Desktop: user dropdown */}
                    {!isMobile && (
                        <>
                            {user ? (
                                <div ref={dropRef} style={{ position: 'relative' }}>
                                    <button onClick={() => setDropOpen(!dropOpen)} style={{ background: 'transparent', border: '0.5px solid #3a4e64', color: '#e8dfc8', padding: '5px 12px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#8b1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600 }}>
                                            {user.first_name?.[0]}{user.last_name?.[0]}
                                        </span>
                                        {user.first_name}
                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 4l4 4 4-4" stroke="#b8ad98" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                    </button>

                                    {dropOpen && (
                                        <div style={{ position: 'absolute', right: 0, top: '38px', background: '#1e2a3a', border: '0.5px solid #3a4e64', borderRadius: '4px', minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 300 }}>
                                            <div style={{ padding: '8px 12px', borderBottom: '0.5px solid #2e3d50', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                                                {user.email}
                                            </div>
                                            {[
                                                { to: '/account',                           label: 'My account'        },
                                                { to: '/account', state: {tab:'chapters'},  label: 'My chapters'       },
                                                { to: '/premium',                           label: '⭐ Premium membership' },
                                                ...(isAdmin ? [{ to: '/admin', label: '⚙ Admin dashboard' }] : []),
                                                ...(user.role === 'contributor' || user.role === 'admin' || user.role === 'superadmin' ? [{ to: '/submit-article', label: '✍ Submit article' }] : [])
                                            ].map(({ to, label, state: linkState }) => (
                                                <Link key={label} to={to} state={linkState} onClick={() => setDropOpen(false)}
                                                    style={{ display: 'block', padding: '8px 12px', fontSize: '12px', fontFamily: 'sans-serif', color: '#c8beaa', textDecoration: 'none', letterSpacing: '0.04em' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#2e3d50'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                    {label}
                                                </Link>
                                            ))}
                                            <div style={{ borderTop: '0.5px solid #2e3d50', padding: '4px' }}>
                                                <button onClick={handleLogout} style={{ width: '100%', background: 'transparent', border: 'none', padding: '8px 12px', fontSize: '12px', fontFamily: 'sans-serif', color: '#c8beaa', cursor: 'pointer', textAlign: 'left', letterSpacing: '0.04em', borderRadius: '2px' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#2e3d50'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                    Sign out
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Link to="/login" style={{ color: '#b8ad98', fontSize: '12px', fontFamily: 'sans-serif', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                        Sign in
                                    </Link>
                                    <Link to="/register" state={{ backgroundLocation: location }} style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '5px 12px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                                        Join
                                    </Link>
                                </div>
                            )}
                        </>
                    )}

                    {/* Mobile: hamburger button */}
                    {isMobile && (
                        <button onClick={() => setMenuOpen(!menuOpen)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', flexDirection: 'column', gap: '5px' }}
                            aria-label="Menu">
                            <span style={{ display: 'block', width: '22px', height: '1.5px', background: '#e8dfc8', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }} />
                            <span style={{ display: 'block', width: '22px', height: '1.5px', background: '#e8dfc8', opacity: menuOpen ? 0 : 1, transition: 'opacity 0.2s' }} />
                            <span style={{ display: 'block', width: '22px', height: '1.5px', background: '#e8dfc8', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }} />
                        </button>
                    )}
                </div>
            </nav>

            {/* Mobile menu drawer */}
            {isMobile && (
                <>
                    {/* Backdrop */}
                    {menuOpen && (
                        <div onClick={() => setMenuOpen(false)}
                            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 490, top: '56px' }} />
                    )}

                    {/* Drawer */}
                    <div style={{
                        position:   'fixed',
                        top:        '56px',
                        left:       0,
                        right:      0,
                        background: '#1e2a3a',
                        zIndex:     495,
                        transform:  menuOpen ? 'translateY(0)' : 'translateY(-100%)',
                        transition: 'transform 0.25s ease',
                        borderBottom: '2px solid #8b1a1a',
                        maxHeight:  'calc(100vh - 56px)',
                        overflowY:  'auto'
                    }}>
                        {/* Nav links */}
                        <div style={{ padding: '8px 0', borderBottom: '0.5px solid #2e3d50' }}>
                            {NAV_LINKS.map(({ to, label }) => (
                                <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                                    style={{ display: 'block', padding: '12px 1.5rem', fontSize: '15px', fontFamily: "'EB Garamond', Georgia, serif", color: '#e8dfc8', textDecoration: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
                                    {label}
                                </Link>
                            ))}
                        </div>

                        {/* User section */}
                        {user ? (
                            <div>
                                <div style={{ padding: '10px 1.5rem', background: '#172030', fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa' }}>
                                    Signed in as <strong style={{ color: '#e8dfc8' }}>{user.first_name} {user.last_name}</strong>
                                </div>
                                {[
                                    { to: '/account',                          label: 'My account'        },
                                    { to: '/account',  state:{tab:'chapters'}, label: 'My chapters'       },
                                    { to: '/premium',                          label: '⭐ Premium membership' },
                                    ...(isAdmin ? [{ to: '/admin', label: '⚙ Admin dashboard' }] : []),
                                    ...(user.role === 'contributor' || user.role === 'admin' || user.role === 'superadmin' ? [{ to: '/submit-article', label: '✍ Submit article' }] : [])
                                ].map(({ to, label, state: linkState }) => (
                                    <Link key={label} to={to} state={linkState} onClick={() => setMenuOpen(false)}
                                        style={{ display: 'block', padding: '12px 1.5rem', fontSize: '14px', fontFamily: 'sans-serif', color: '#c8beaa', textDecoration: 'none', borderBottom: '0.5px solid rgba(255,255,255,0.05)' }}>
                                        {label}
                                    </Link>
                                ))}
                                <button onClick={handleLogout}
                                    style={{ width: '100%', background: 'transparent', border: 'none', padding: '12px 1.5rem', fontSize: '14px', fontFamily: 'sans-serif', color: '#8b9a7a', cursor: 'pointer', textAlign: 'left', borderTop: '0.5px solid #2e3d50' }}>
                                    Sign out
                                </button>
                            </div>
                        ) : (
                            <div style={{ padding: '1rem 1.5rem', display: 'flex', gap: '10px' }}>
                                <Link to="/login" onClick={() => setMenuOpen(false)}
                                    style={{ flex: 1, background: 'transparent', border: '0.5px solid #3a4e64', color: '#e8dfc8', padding: '10px', fontSize: '13px', fontFamily: 'sans-serif', textDecoration: 'none', borderRadius: '3px', textAlign: 'center', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                    Sign in
                                </Link>
                                <Link to="/register" state={{ backgroundLocation: location }} onClick={() => setMenuOpen(false)}
                                    style={{ flex: 1, background: '#8b1a1a', color: '#f5f0e8', padding: '10px', fontSize: '13px', fontFamily: 'sans-serif', textDecoration: 'none', borderRadius: '3px', textAlign: 'center', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                    Join
                                </Link>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default Nav;
